/**
 * Vertical / horizontal timeline — drawing connector + pop-in milestone dots.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { splitItem, sceneHeader } from "./_mockHelpers.js";

export const timelineFlow: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const eyebrow = inputs.subtext ?? "";
  const items = (inputs.items?.length ? inputs.items : ["2024|Idee|Start", "2025|MVP|First users", "2026|Launch|Public", "2027|Scale|DACH"]).slice(0, 6);
  const isVertical = ctx.format === "9:16";

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const xs = typeSize("xs", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const lineId = `${clipPrefix}-line`;
  const dotIds = items.map((_, i) => `${clipPrefix}-dot-${i}`);
  const cardIds = items.map((_, i) => `${clipPrefix}-card-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const header = sceneHeader({ clipPrefix, accent, eyebrow, title: headline, titlePx: lg * 0.72, subPx: sm * 0.95 });

  const stepHtml = items.map((raw, i) => {
    const [date, title, desc] = splitItem(raw);
    const alignRight = !isVertical || i % 2 === 1;
    return `<div style="position:relative;display:flex;flex-direction:${isVertical ? "row" : "column"};align-items:center;gap:${isVertical ? 22 : 14}px;${isVertical ? "" : "flex:1;"}">
      <div id="${dotIds[i]}" style="position:relative;width:42px;height:42px;border-radius:9999px;background:${accent};box-shadow:0 0 30px ${withAlpha(accent, 0.65)},inset 0 0 0 4px ${themeColors.bg};display:flex;align-items:center;justify-content:center;color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:14px;flex-shrink:0;z-index:2;">${i + 1}</div>
      <div id="${cardIds[i]}" style="background:linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.07)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1.5px solid ${withAlpha(themeColors.fg, 0.12)};border-radius:24px;padding:18px 24px;display:flex;flex-direction:column;gap:6px;${isVertical ? "flex:1;" : "width:100%;"};box-shadow:0 14px 30px rgba(0,0,0,0.35);">
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${xs * 0.95}px;letter-spacing:3px;text-transform:uppercase;color:${accent};">${escapeHtml(date ?? "")}</div>
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.55}px;letter-spacing:-0.5px;color:${themeColors.fg};line-height:1.05;">${escapeHtml(title ?? "")}</div>
        ${desc ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.7}px;color:${withAlpha(themeColors.fg, 0.7)};line-height:1.35;">${escapeHtml(desc)}</div>` : ""}
      </div>
    </div>`;
  }).join("");

  // The connector — vertical absolute line on the left in portrait, horizontal across the row otherwise.
  const railHtml = isVertical
    ? `<div style="position:absolute;left:21px;top:0;bottom:0;width:2px;background:${withAlpha(themeColors.fg, 0.1)};border-radius:2px;"><div id="${lineId}" style="position:absolute;left:0;top:0;width:100%;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.3)} 100%);height:0%;box-shadow:0 0 20px ${withAlpha(accent, 0.55)};"></div></div>`
    : `<div style="position:absolute;left:0;right:0;top:21px;height:2px;background:${withAlpha(themeColors.fg, 0.1)};border-radius:2px;"><div id="${lineId}" style="position:absolute;left:0;top:0;height:100%;background:linear-gradient(90deg, ${accent} 0%, ${withAlpha(accent, 0.3)} 100%);width:0%;box-shadow:0 0 20px ${withAlpha(accent, 0.55)};"></div></div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:32px;padding:72px;">
    ${header.html}
    <div style="position:relative;display:flex;flex-direction:${isVertical ? "column" : "row"};gap:${isVertical ? 22 : 16}px;align-items:${isVertical ? "stretch" : "flex-start"};width:100%;max-width:${isVertical ? 820 : 1280}px;">
      ${railHtml}
      ${stepHtml}
    </div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${header.ids.eyebrow}`, { opacity: 0, y: 12 }) : "",
    headline ? gsapSetLine(`#${header.ids.title}`, { opacity: 0, y: 16 }) : "",
    gsapSetLine(`#${lineId}`, isVertical ? { height: "0%" } : { width: "0%" }),
    ...dotIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0.4 })),
    ...cardIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, x: isVertical ? 30 : 0, y: isVertical ? 0 : 20 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${header.ids.eyebrow}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.0));
  if (headline) tweens.push(t.to(`#${header.ids.title}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.15));
  const lineDur = Math.max(1.0, clipDurationSec * 0.55);
  tweens.push(t.to(`#${lineId}`, isVertical ? { height: "100%", duration: lineDur, ease: "raw:\"power2.inOut\"" } : { width: "100%", duration: lineDur, ease: "raw:\"power2.inOut\"" }, 0.4));
  const stagger = Math.max(0.35, lineDur / Math.max(items.length, 1));
  dotIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, scale: 1, duration: 0.45, ease: "raw:\"back.out(2)\"" }, 0.45 + i * stagger)));
  cardIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, x: 0, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.55 + i * stagger)));
  if (dotIds.length) tweens.push(t.to(`#${dotIds[dotIds.length - 1]}`, { scale: 1.2, duration: 0.6, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 0.5 + (items.length) * stagger));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
